import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'regen_cookie_consent';
const CONSENT_VERSION = 1;
export const OPEN_COOKIE_PREFS_EVENT = 'regen:open-cookie-prefs';

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c.version !== CONSENT_VERSION) return null;
    return c;
  } catch { return null; }
}

function saveConsent(prefs) {
  const record = { ...prefs, necessary: true, version: CONSENT_VERSION, ts: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('regen:cookie-consent-updated', { detail: record }));
  return record;
}

export function CookieConsent() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const barRef = useRef(null);

  // Marketing website only — hide inside the logged-in app and admin.
  const path = location.pathname || '/';
  const isMarketing = !path.startsWith('/app') && !path.startsWith('/admin');

  useEffect(() => {
    if (!readConsent()) setShowBanner(true);
  }, []);

  // Publish the banner height so fixed form footers (e.g. the onboarding "Continue" bar) can sit above it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      if (showBanner && barRef.current) root.style.setProperty('--cookie-bar', barRef.current.offsetHeight + 'px');
      else root.style.setProperty('--cookie-bar', '0px');
    };
    apply();
    window.addEventListener('resize', apply);
    return () => { window.removeEventListener('resize', apply); root.style.setProperty('--cookie-bar', '0px'); };
  }, [showBanner]);

  useEffect(() => {
    const open = () => {
      const c = readConsent();
      setAnalytics(c ? !!c.analytics : true);
      setMarketing(c ? !!c.marketing : true);
      setShowPrefs(true);
    };
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, open);
  }, []);

  const acceptAll = () => { saveConsent({ analytics: true, marketing: true }); setShowBanner(false); setShowPrefs(false); };
  const rejectAll = () => { saveConsent({ analytics: false, marketing: false }); setShowBanner(false); setShowPrefs(false); };
  const savePrefs = () => { saveConsent({ analytics, marketing }); setShowBanner(false); setShowPrefs(false); };

  if (!isMarketing) return null;

  return (
    <>
      {showBanner && (
        <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[90] pointer-events-none">
          <div ref={barRef}
            className="pointer-events-auto mx-auto w-full sm:max-w-3xl sm:mb-3 border-t sm:border border-border bg-card/97 text-card-foreground backdrop-blur-xl shadow-[0_-8px_40px_rgba(8,40,80,0.16)] sm:rounded-2xl px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-[#00C8FF]/15 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 text-[#00B4C7]" />
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                <span className="font-semibold text-foreground">We value your privacy. </span>
                We use cookies to run the site and improve your experience.{' '}
                <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#00B4C7] underline">Privacy Policy</a>.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0 self-end sm:self-auto">
              <Button data-testid="cookie-customize" onClick={() => setShowPrefs(true)} variant="ghost"
                className="h-9 px-3 rounded-lg text-xs text-muted-foreground hover:text-foreground">
                Customize
              </Button>
              <Button data-testid="cookie-reject-all" onClick={rejectAll} variant="outline"
                className="h-9 px-4 border-border rounded-lg text-xs">
                Reject
              </Button>
              <Button data-testid="cookie-accept-all" onClick={acceptAll}
                className="h-9 px-4 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-lg text-xs">
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="cookie-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl">Cookie Preferences</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Manage how we use cookies. Necessary cookies are always on.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <CookieRow title="Strictly Necessary" testid="cookie-toggle-necessary" locked
              desc="Required for the site to function — security, session and consent storage.">
              <div className="flex items-center gap-1.5 text-[11px] text-[var(--c-green-ink)]"><ShieldCheck className="w-3.5 h-3.5" /> Always on</div>
            </CookieRow>
            <CookieRow title="Analytics" testid="cookie-toggle-analytics"
              desc="Help us understand how visitors use the site so we can improve it.">
              <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-switch-analytics" />
            </CookieRow>
            <CookieRow title="Marketing" testid="cookie-toggle-marketing"
              desc="Used to deliver relevant offers and measure campaign performance.">
              <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="cookie-switch-marketing" />
            </CookieRow>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button data-testid="cookie-save-prefs" onClick={savePrefs}
              className="h-11 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">Save preferences</Button>
            <Button data-testid="cookie-accept-all-dialog" onClick={acceptAll} variant="outline"
              className="h-11 border-border rounded-xl">Accept all</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CookieRow({ title, desc, children, testid, locked }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl bg-muted/40 border border-border p-3.5" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{desc}</div>
      </div>
      <div className="flex-shrink-0 pt-0.5">{children}</div>
    </div>
  );
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFS_EVENT));
}
